"use client";
import { ArrowLeft, ArrowRight } from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";
import { useLang } from "@/app/context/LangContext";
import { SupportedLanguages } from "@/types/dictionary";

interface Testimonial {
  text: string;
  author: string;
  role: string;
}

interface CarouselComponentsProps {
  testimonials: Testimonial[];
}

const CarouselComponents: React.FC<CarouselComponentsProps> = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const isRTL = useLang() === SupportedLanguages.HE;

  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

  const dots: React.ReactNode[] = [];
  const AUTO_SCROLL_INTERVAL_MS = 3000;


useEffect(() => {
  const interval = setInterval(() => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  }, AUTO_SCROLL_INTERVAL_MS);

  return () => clearInterval(interval);
}, [testimonials.length]);

  return (
    <div className="w-full sm:py-10 py-5 flex flex-col items-center justify-center">
      <div className="relative w-full max-w-2xl px-3 sm:px-6 flex flex-col items-center justify-center">
        <div className="relative sm:w-full w-[95%] flex items-center justify-center">
          <div onClick={handlePrev} className="bg-gray5 sm:text-[1rem] text-[0.6rem] rounded-[0.5rem] sm:p-2.5 p-1 hover:bg-green1 text-gray6 hover:text-gray1 absolute left-[-1.5rem] md:left-[-4rem] sm:left-[-3rem] lg:left-[-11rem] xl:left-[-11rem] top-1/2 transform -translate-y-1/2">
            <ArrowLeft className="h-6 w-6" />
          </div>
          <Carousel className="relative w-[90%] sm:w-full overflow-hidden">
          <CarouselContent dir="ltr" className="flex transition-transform duration-500 min-h-[250px]"  
              style={{ transform: `translateX(${isRTL ? -currentIndex * 100 : -currentIndex * 100}%)` }}>
              
              {testimonials?.map((testimonial, index) => {
                dots.push(
                  <span
                    key={index}
                    className={`w-2 h-2 rounded-full transition-all duration-300  ${
                      index === currentIndex ? "bg-green1 scale-110" : "bg-gray3"
                    }`}
                    onClick={() => setCurrentIndex(index)}
                  ></span>
                );

                return (
                  <CarouselItem key={index} className="w-full flex-shrink-0 ">
                    <div className={`sm:p-12 p-2 py-5 sm:py-10 bg-gray1 shadow-md rounded-[1rem] ${isRTL ? 'text-right' : 'text-left'} min-h-[220px] flex flex-col justify-between relative`}>
                      <h3 className="sm:text-lg text-[1.4rem] px-2 font-rubik font-bold text-slate1 mb-2" dir={isRTL ? "rtl" : "ltr"}>
                        {testimonial.author} 
                      </h3>
                      <p className={`text-[1rem] text-gray4 font-rubik font-400 relative inline-block px-2 after:content-[''] after:block ${isRTL ? 'after:right-0' : 'after:left-0'} sm:after:w-[25%] after:w-[60%] after:h-[1px] after:bg-gray5 after:mt-4`} dir={isRTL ? "rtl" : "ltr"}>
                        {testimonial.role}
                      </p>
                      <p className="font-rubikItalic font-light text-[1rem] sm:text-[1.05rem] text-slate1 mt-4 flex-grow leading-[1.3rem] px-2 " dir={isRTL ? "rtl" : "ltr"}>
                        {testimonial.text}
                      </p>
                    </div>
                  </CarouselItem>
                );
              })}

            </CarouselContent >
          </Carousel>
          <div onClick={handleNext} className="bg-gray5 sm:text-[1rem] text-[0.6rem] rounded-[0.5rem] sm:p-2.5 p-1 hover:bg-green1 text-gray6 hover:text-gray1 absolute right-[-1.5rem] md:right-[-4rem] sm:right-[-3rem] lg:right-[-11rem] xl:right-[-11rem] top-1/2 transform -translate-y-1/2">
            <ArrowRight className="h-6 w-6" />
          </div>
        </div>

        <div dir="ltr" className="flex space-x-2 mt-1 sm:mt-8">{dots}</div>
      </div>
    </div>
  );
};

export default CarouselComponents;
